import { Box, Typography, Paper } from "@mui/material";
import Form from "@rjsf/mui";
import type { RJSFSchema } from "@rjsf/utils";
import type { WidgetProps } from "@rjsf/utils";
import validator from "@rjsf/validator-ajv8";
import type { IChangeEvent } from "@rjsf/core";
import FileUrlWidget from "../../components/widgets/FileUrlWidget";
import MarkdownEditor from "../../components/cms/MarkdownEditor";
import { useMemo } from "react";
import ArrayFieldItemTemplate from "./ArrayFieldItemTemplate";
import ObjectFieldTemplate from "./ObjectFieldTemplate";

// Custom widget for markdown editing (no changes needed here)
const MarkdownWidget = (props: WidgetProps) => {
  const { id, value, disabled, readonly, label, schema, onChange, onBlur } =
    props;

  return (
    <MarkdownEditor
      value={value || ""}
      onChange={(newValue) => {
        onChange(newValue);
        onBlur(id, newValue);
      }}
      label={label || schema.title || "Answer"}
      disabled={disabled || readonly}
      rows={8}
    />
  );
};

// UPDATED: Added layoutColumns to the props interface
interface JsonContentEditorProps {
  schema: RJSFSchema;
  data: any;
  onChange: (data: any) => void;
  title?: string;
  description?: string;
  disabled?: boolean;
  uiSchema?: any;
}

const JsonContentEditor: React.FC<JsonContentEditorProps> = ({
  schema,
  data,
  onChange,
  title,
  description,
  disabled = false,
  uiSchema = {},
}) => {
  const handleChange = (e: IChangeEvent<any>) => {
    onChange(e.formData);
  };

  // UPDATED: createUiSchema is now more robust and handles layout
  const createUiSchema = useMemo(() => {
    const baseUiSchema: any = {
      "ui:rootFieldId": "content",
      "ui:submitButtonOptions": {
        norender: true,
      },
      "ui:classNames": "ai11-jce-container", // Applies to form
    };

    // Determine the target for uiSchema modifications (top-level or array items)
    let properties: RJSFSchema["properties"] | undefined = undefined;
    let uiSchemaTarget: any = {};

    if (schema.type === "object") {
      properties = schema.properties;
      uiSchemaTarget = baseUiSchema;
    } else if (
      schema.type === "array" &&
      schema.items &&
      typeof schema.items === "object" &&
      !Array.isArray(schema.items)
    ) {
      properties = schema.items.properties;
      baseUiSchema.items = {
        "ui:classNames": "ai11-jce-items",
        // "ui:field": "LayoutGridField",
        // "ui:layoutGrid": {
        //   "ui:row": {
        //     className: "ai11-jce-row",
        //     children: [
        //       {
        //         "ui:col": {
        //           className: "MuiGrid-item MuiGrid-grid-xs-12 MuiGrid-grid-md-6",
        //           children: ["title"],
        //         },
        //       },
        //       {
        //         "ui:col": {
        //           className: "MuiGrid-item MuiGrid-grid-xs-12 MuiGrid-grid-md-6",
        //           children: ["subtitle"],
        //         },
        //       },
        //     ],
        //   },
        // },
      };
      uiSchemaTarget = baseUiSchema.items;
    }

    if (!properties) {
      // console.log("no properties", baseUiSchema);
      return baseUiSchema;
    }

    // Apply specific widgets and layout based on properties
    Object.keys(properties).forEach((fieldName) => {
      const fieldSchema = properties![fieldName] as RJSFSchema;

      // Ensure a uiSchema object exists for the current field
      if (!uiSchemaTarget[fieldName]) {
        uiSchemaTarget[fieldName] = {};
      }

      // 1. Assign custom widgets based on format
      // if (fieldSchema.format === 'markdown') {
      //   uiSchemaTarget[fieldName]['ui:widget'] = 'markdown';
      //   uiSchemaTarget[fieldName]['ui:options'] = {
      //     label: fieldSchema.title || fieldName
      //   };
      // } else if (fieldSchema.format === 'file-url') {
      //   uiSchemaTarget[fieldName]['ui:widget'] = 'FileUrlWidget';
      //   uiSchemaTarget[fieldName]['ui:options'] = {
      //     fileType: 'image'
      //   };
      // }

      // 2. NEW: Apply two-column layout if requested
      // These Material-UI grid classes make the field take up 100% width on extra-small screens (xs-12)
      // and 50% width on medium screens and up (md-6), creating a responsive 2-column grid.
      // if (layoutColumns === 2) {
      if (fieldSchema.format === "markdown") {
        uiSchemaTarget[fieldName]["ui:options"] = {
          classNames: "ai11-jce-item ai11-jce-item-full-width",
        };
      }else{
        uiSchemaTarget[fieldName]["ui:options"] = {
          // classNames: "ai11-jce-item MuiGrid-root MuiGrid-direction-xs-row MuiGrid-grid-xs-12 MuiGrid-grid-md-6 css-204a8q-MuiGrid-root",
          classNames: "ai11-jce-item ai11-jce-item-half-width",
        };
      }
      // }
    });
    // console.log("fullschema", baseUiSchema);
    return baseUiSchema;
  }, [schema]);

  return (
    <Paper sx={{ p: 2 }} elevation={0} variant="outlined">
      {title && (
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
      )}
      {description && (
        <Typography variant="body2" color="text.secondary" paragraph>
          {description}
        </Typography>
      )}

      <Box
        className="json-content-editor-container"
        // sx={{
        //   "& .MuiGrid-root .MuiGrid-direction-xs-row": {
        //     maxWidth: "100%",
        //   },
        // }}
      >
        <Form
          schema={schema}
          formData={data || {}}
          validator={validator}
          onChange={handleChange}
          liveValidate={false}
          noHtml5Validate
          disabled={disabled}
          widgets={{
            // FileUrlWidget: FileUrlWidget,
            "file-url": FileUrlWidget,
            markdown: MarkdownWidget,

            // 'MarkdownWidget': MarkdownWidget,
          }}
          uiSchema={{...createUiSchema, ...uiSchema}}
          templates={{
            ArrayFieldItemTemplate,
            ObjectFieldTemplate,
          }}
        />
      </Box>
    </Paper>
  );
};

export default JsonContentEditor;
